<template>
  <LineChart
    ref="chart"
    :unit="unit"
    :scale="scale"
    title="6 Month (%)"
    :loading="loading"
    :daterange="daterange"
  />
</template>

<script>
import {
  mapState,
  mapGetters,
  mapActions,
  mapMutations,
} from 'vuex';

import LineChart from '../LineChart.vue';

export default {
  name: 'OEEMonthwise',
  props: {
    scale: { type: Number, default: 1 },
    unit: { type: String, default: '' },
  },
  components: {
    LineChart,
  },
  data() {
    return {
      daterange: '',
      loading: false,
      config: {},
      machineName: [],
      plotBands: [
        { from: 0, to: 79, color: '#FF5252' },
        { from: 80, to: 85, color: '#FFDA6B' },
        { from: 86, to: 200, color: '#21CE99' },
      ],
    };
  },
  setBandColor() {
    const safeColor = this.config.colors?.safe || '#21CE99';
    const warningColor = this.config.colors?.warning || '#FFDA6B';
    const dangerColor = this.config.colors?.danger || '#FF5252';

    const redLimit = this.config.lessthanred || 70;
    const greenLimit = this.config.greaterthangreen || 80;
    this.plotBands = [
      { from: 0, to: redLimit - 1, color: dangerColor },
      { from: redLimit, to: greenLimit, color: warningColor },
      { from: greenLimit + 1, to: 200, color: safeColor },
    ];
  },
  computed: {
    ...mapState('cellwiseProductionDashboard', ['shifts', 'serverTime', 'dataFromJSON', 'currentDate', 'machines', 'theme', 'userId', 'customerId', 'currentShift']),
    ...mapGetters('cellwiseProductionDashboard', ['firstShift']),
    cellname() {
      return this.$route.query.selectedcell;
    },
  },
  watch: {
    '$route.query.selectedcell': {
      async handler() {
        if (!this.dataFromJSON) {
          await this.fetchData();
        } else if (this.dummydata) {
          await this.dummydata(this.lastdays ? -1 : undefined);
        }
      },
    },
    async currentDate() {
      if (!this.dataFromJSON) {
        this.loading = false;
        await this.fetchData();
      }
    },
  },
  async mounted() {
    if (this.dataFromJSON) {
      await this.dummydata();
    } else {
      await this.fetchData();
    }
  },
  methods: {
    ...mapActions('cellwiseProductionDashboard', ['allShifts', 'getReports', 'getFileData', 'getMachines']),
    ...mapMutations('cellwiseProductionDashboard', ['setShifts']),

    async setBandColor() {
      try {
        const configRes = await this.getFileData('config.json');
        if (configRes) {
          this.config = configRes;
        }
        const redLimit = this.config.lessthanred || 60;
        const greenLimit = this.config.greaterthangreen || 80;
        this.plotBands = [
          { from: 0, to: redLimit - 1, color: '#FF5252' },
          { from: redLimit, to: greenLimit, color: '#FFDA6B' },
          { from: greenLimit + 1, to: 200, color: '#21CE99' },
        ];
      } catch (error) {
        this.plotBands = [
          { from: 0, to: 59, color: '#FF5252' },
          { from: 60, to: 80, color: '#FFDA6B' },
          { from: 81, to: 200, color: '#21CE99' },
        ];
      }
    },
    async fetchData() {
      if (this.loading) return;
      this.loading = true;

      try {
        this.setBandColor();

        let now = new Date();
        if (this.currentDate) {
          const ds = String(this.currentDate);
          if (ds.length === 8 && !ds.includes('-')) {
            now = new Date(ds.slice(0, 4), ds.slice(4, 6) - 1, ds.slice(6, 8));
          } else {
            const parsedDate = new Date(this.currentDate);
            if (!Number.isNaN(parsedDate.getTime()) && parsedDate.getFullYear() > 2020) {
              now = parsedDate;
            }
          }
        }

        const endDateObj = new Date(now.getFullYear(), now.getMonth(), 0);
        const endtimeTs = endDateObj.getTime();

        const startdateObj = new Date(now.getFullYear(), now.getMonth() - 6, 1);
        const starttimeTs = startdateObj.getTime();

        this.setTitle(starttimeTs, endtimeTs);

        // --- YYYYMMDD DATE CONVERSION ---
        const startYYYYMMDD = parseInt(`${startdateObj.getFullYear()}${String(startdateObj.getMonth() + 1).padStart(2, '0')}${String(startdateObj.getDate()).padStart(2, '0')}`, 10);
        const endYYYYMMDD = parseInt(`${endDateObj.getFullYear()}${String(endDateObj.getMonth() + 1).padStart(2, '0')}${String(endDateObj.getDate()).padStart(2, '0')}`, 10);

        if (!this.machines || this.machines.length === 0) {
          await this.getMachines();
        }

        let filteredMachines = this.machines || [];
        if (this.cellname) {
          filteredMachines = filteredMachines.filter((m) => m.machinecell === this.cellname);
        }
        this.machinenames = filteredMachines.length > 0
          ? filteredMachines.map((m) => m.machinename)
          : [];

        let activeSite = this.$store.state.user?.activeSite
         || this.$store.state.auth?.user?.site_id;
        if (!activeSite) {
          try {
            const swxUser = JSON.parse(localStorage.getItem('user') || '{}');
            activeSite = swxUser?.site_id || swxUser?.site?.id || swxUser?.customer?.site_id;
          } catch (e) { /* ignore */ }
        }
        if (!activeSite && this.machines?.length > 0) {
          activeSite = this.machines[0].siteId || this.machines[0].siteid;
        }

        if (!activeSite) {
          this.setZeroState();
          return;
        }

        const payload = {
          siteid: activeSite,
          start: startYYYYMMDD,
          end: endYYYYMMDD,
          singledaterange: false,
          all: true,
        };

        const reportName = 'dailymachineperformance';
        const result = await this.getReports({ reportName, payload });

        if (result) {
          const parsedResult = typeof result === 'string' ? JSON.parse(result) : result;
          const finalData = parsedResult.reportData || parsedResult;

          if (finalData && finalData.length > 0) {
            this.setOptions(finalData);
          } else {
            this.setZeroState();
          }
        } else {
          this.setZeroState();
        }
      } catch (e) {
        this.setZeroState();
      } finally {
        this.loading = false;
      }
    },
    setOptions(data) {
      if (!data || !Array.isArray(data)) return;

      let baseDate = new Date();
      if (this.currentDate) {
        const ds = String(this.currentDate);
        if (ds.length === 8 && !ds.includes('-')) {
          baseDate = new Date(ds.slice(0, 4), ds.slice(4, 6) - 1, ds.slice(6, 8));
        } else {
          const parsedDate = new Date(this.currentDate);
          if (!Number.isNaN(parsedDate.getTime()) && parsedDate.getFullYear() > 2020) {
            baseDate = parsedDate;
          }
        }
      }

      const categories = [];
      const monthMap = {};

      for (let i = 6; i >= 1; i -= 1) {
        const d = new Date(baseDate.getFullYear(), baseDate.getMonth() - i, 1);
        const monthLabel = d.toLocaleString('en-US', { month: 'short' });
        const yearLabel = d.getFullYear().toString().slice(-2);
        const label = `${monthLabel} '${yearLabel}`;
        categories.push(label);
        monthMap[label] = { sum: 0, count: 0 };
      }

      data.forEach((d) => {
        if (this.machinenames.includes(d.machinename) && d.date) {
          let rowDate;
          const parts = String(d.date).split('-');

          if (parts.length === 3 && parts[2].length === 4) {
            rowDate = new Date(parts[2], parts[1] - 1, parts[0]);
          } else if (parts.length === 3 && parts[0].length === 4) {
            rowDate = new Date(parts[0], parts[1] - 1, parts[2]);
          }

          if (rowDate) {
            const mLabel = rowDate.toLocaleString('en-US', { month: 'short' });
            const yLabel = rowDate.getFullYear().toString().slice(-2);
            const label = `${mLabel} '${yLabel}`;

            if (monthMap[label]) {
              monthMap[label].sum += (d.oee || d.OEE || 0);
              monthMap[label].count += 1;
            }
          }
        }
      });

      const seriesData = categories.map((label) => {
        const stat = monthMap[label];
        let avg = 0;
        if (stat.count > 0) {
          avg = Math.round((stat.sum / stat.count) * 100) / 100;
        }
        return avg;
      });

      const options = {
        categories,
        series: [{ name: 'OEE (%)', data: seriesData }],
        plotBands: this.plotBands,
      };

      if (this.$refs.chart) {
        this.$refs.chart.assignData(options);
      }
    },
    setZeroState() {
      const series = [{ name: 'OEE:', data: [] }];
      if (this.$refs.chart) {
        this.$refs.chart.assignData({ categories: [], series, plotBands: this.plotBands });
      }
    },

    startTime(shift) {
      if (!shift || !shift.starttimestamp) return new Date().getTime();
      const startdate = new Date(shift.starttimestamp);
      startdate.setMonth(startdate.getMonth() - 6);
      startdate.setDate(1);
      return startdate.getTime();
    },

    endTime(shift) {
      if (!shift || !shift.starttimestamp) return new Date().getTime();
      const startdate = new Date(shift.starttimestamp);
      startdate.setDate(1);
      return startdate.getTime() - 86399999;
    },

    setTitle(start, end) {
      this.daterange = `${this.dateFormat(start)} - ${this.dateFormat(end)}`;
    },

    dateFormat(val) {
      if (!val) return '';
      try {
        const date = new Date(val);
        const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
        const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
        const month = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(date);
        return `${day}/${month}/${year}`;
      } catch (e) {
        return '';
      }
    },
  },
};
</script>
